// `client-contact-search.page.tpl.html:8-199` — the contact builder's first section: a
// switched-company toggle, a previous-engagement picker and a repeating job-title row.
import type { ReactElement } from 'react'
import type { TitleRecommendation } from '@woo-ext/api'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { TitleSuggestionPanel } from './TitleSuggestionPanel.tsx'
import { CategoryTip, TitleRecommendations, showCategoryTip } from './TitleRecommendations.tsx'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { tagsPlaceholder } from './SkillSetSection.tsx'

/** One row of `titleCriteria.titles` — the section repeats it and joins the rows with AND. */
export interface ContactTitleRow {
  titles?: readonly Tag[]
}

export interface ContactFiltersSectionProps {
  /** `switchedCompanyCriteria.enabled` — reveals the two year inputs when on. */
  switchedCompany?: boolean
  switchedCompanyMin?: string
  switchedCompanyMax?: string
  /** `highestActivityLevelCriteria.values`. */
  previousEngagement?: readonly Tag[]
  /** `titleCriteria.titles`; always at least one row, so the field is never absent. */
  titleRows?: readonly ContactTitleRow[]
  onSwitchedCompanyChange?: (enabled: boolean) => void
  /**
   * ⚠️ THE FOUR HANDLERS BELOW WERE MISSING AND THE SECTION WAS THEREFORE READ-ONLY. Both
   * year inputs carried `readOnly`, and the two tag fields took no `onChange` at all, so the
   * section rendered an editable-looking form that could not be edited — which is exactly
   * what "some sections are uneditable" means. The suggestion lists are still the
   * recommendations service's, which is a separate port; free text is what upstream's
   * `allow-free-text` on these two fields already permits.
   */
  onSwitchedCompanyMinChange?: (value: string) => void
  onSwitchedCompanyMaxChange?: (value: string) => void
  onPreviousEngagementChange?: (tags: readonly Tag[]) => void
  onTitlesChange?: (index: number, tags: readonly Tag[]) => void
  /** `getHighestActivityLevelsAutocompleteValues($query)` — `add-from-autocomplete-only`. */
  previousEngagementSuggestions?: readonly Tag[]
  onPreviousEngagementQueryChange?: (query: string) => void
  /** `typeaheads.getHighestActivityLevelsAutocompleteValues($query, true)`. */
  loadPreviousEngagement?: (query: string) => Promise<readonly Tag[]>
  /** `ctrl.getJobTitles($query, titles.titles)` — the row's own titles are excluded. */
  loadTitles?: (index: number, query: string) => Promise<readonly Tag[]>
  /**
   * `titles.titleRecommendations`, per row — the Suggestions block under the Job title field.
   *
   * ⚠️ THE CONTACT FORM HAS NO FEATURE-FLAG GUARD ON IT. The candidate builder's block is
   * behind `extendedTitleClassUXFeatureEnabled`; `client-contact-search.page.tpl.html:118`
   * tests only `titles.titles.length > 0`, so the block is unconditional here.
   */
  recommendations?: Readonly<Record<number, readonly TitleRecommendation[]>>
  recommendationsLoading?: boolean
  onSelectRecommendation?: (index: number, title: string) => void
  onAddTitleRow?: () => void
  onRemoveTitleRow?: (index: number) => void
}

export function ContactFiltersSection({
  switchedCompany = false,
  switchedCompanyMin = '',
  switchedCompanyMax = '',
  previousEngagement,
  titleRows = [{}],
  onSwitchedCompanyChange,
  onSwitchedCompanyMinChange,
  onSwitchedCompanyMaxChange,
  onPreviousEngagementChange,
  onTitlesChange,
  previousEngagementSuggestions,
  onPreviousEngagementQueryChange,
  loadPreviousEngagement,
  loadTitles,
  recommendations = {},
  recommendationsLoading = false,
  onSelectRecommendation,
  onAddTitleRow,
  onRemoveTitleRow,
}: ContactFiltersSectionProps): ReactElement {
  return (
    <>
      <div className="switched-companies-wrap">
        <div className="switched-companies-dates-container sub-section mB10">
          <div className="switched-companies-checkbox-container">
            <WooCheckbox
              checked={switchedCompany}
              onChange={(next) => {
                onSwitchedCompanyChange?.(next)
              }}
            >
              Switched company
            </WooCheckbox>{' '}
            {/* ⚠️ THE SPACE BEFORE THE ICON IS A TEXT NODE, fourth time in this port. Upstream
                has a newline between `</checkbox>` and `<i>`, collapsed to one space by the
                inline formatting context; JSX drops whitespace between elements on separate
                lines, and the icon sat 4px left of where production has it.
                The tooltip text comes from the controller and is not in the DOM until hover. */}
            <i className="fas fa-info-circle" />
          </div>
          {/* ⚠️ THE TWO YEAR FIELDS ARE ABSENT, NOT DISABLED, until the box is ticked — two
              separate `ng-if`s on the SAME flag, so they appear and disappear together and the
              section's height changes with them. */}
          {!switchedCompany ? null : (
            <div className="switched-companies-from">
              <input
                name="switchedCompaniesCriteriaMin"
                type="number"
                className="form-control input"
                placeholder="From year"
                value={switchedCompanyMin}
                onChange={(event) => {
                  onSwitchedCompanyMinChange?.(event.target.value)
                }}
              />
            </div>
          )}
          {!switchedCompany ? null : (
            <div className="switched-companies-to">
              <input
                name="switchedCompaniesCriteriaMax"
                type="number"
                className="form-control input"
                placeholder="To year"
                value={switchedCompanyMax}
                onChange={(event) => {
                  onSwitchedCompanyMaxChange?.(event.target.value)
                }}
              />
            </div>
          )}
        </div>
      </div>

      <div className="previous-engagement-criteria sub-section">
        <div className="previous-engagement-container">
          <div className="form-group previous-engagement-size mT0">
            <label className="bold-soft">Previous engagement</label>
            {/* `or-in-between` renders "OR" between tags; the magnifier is the
                `tags-input-indicator`, shown only while the field is EMPTY. */}
            <TagsInput
              // `name="previousEngagement"` / `name="titles{{$index}}"` are upstream's, and
              // they are not decoration: the form's validation messages address the fields by
              // name (`ctrl.form.titleCriteria['titles' + $index].$error`).
              name="previousEngagement"
              className="or-in-between"
              placeholder={tagsPlaceholder(previousEngagement, 'previous engagement')}
              tags={previousEngagement ?? []}
              indicator={previousEngagement === undefined || previousEngagement.length === 0}
              indicatorClassName="fas fa-search"
              {...(previousEngagementSuggestions === undefined ? {} : { suggestions: previousEngagementSuggestions })}
              {...(onPreviousEngagementQueryChange === undefined
                ? {}
                : { onQueryChange: onPreviousEngagementQueryChange })}
              {...(loadPreviousEngagement === undefined ? {} : { loadSuggestions: loadPreviousEngagement })}
              // ⚠️ `load-on-empty` AND `load-on-focus`, BOTH UPSTREAM'S
              // (`client-contact-search.page.tpl.html:63-64`) AND BOTH LOAD-BEARING HERE.
              // The six values this field accepts are phrases nobody types from memory —
              // "Their company had hiring processes" — so production opens the whole list the
              // moment the field is clicked. Without them the field answered nothing until a
              // matching letter was typed, which is indistinguishable from a broken loader.
              loadOnEmpty
              loadOnFocus
              onAdd={(tag) => {
                onPreviousEngagementChange?.([...(previousEngagement ?? []), tag])
              }}
              onRemove={(index) => {
                onPreviousEngagementChange?.((previousEngagement ?? []).filter((_, at) => at !== index))
              }}
            />
          </div>
        </div>
      </div>

      <div className="field-of-expertise-wrap field-of-expertise sub-section">
        <div className="field-of-expertise-criteria criteria-table">
          <div className="criteria-row">
            <div className="criteria-cell-label">
              <label className="bold-soft">Job title</label>
            </div>
          </div>
          {titleRows.map((row, index) => (
            // eslint-disable-next-line react/no-array-index-key -- `ng-repeat` is positional.
            <div className="criteria-row sub-section" key={index}>
              <div className="criteria-cell">
                <div className="other-positions-wrap">
                  {/* ⚠️ A THIRD ARGUMENT, AND IT IS NOT "Add job title". The placeholder is
                      "Add position (title)" — the only call in either builder that overrides
                      the `Add ${noun}` default, and the noun it would otherwise use is
                      "title". */}
                  <TagsInput
                    name={`titles${String(index)}`}
                    className="client-contact-tags-input-width or-in-between tags-input-extended-title-class-ux-recommendations"
                    placeholder={tagsPlaceholder(row.titles, 'title', 'Add position (title)')}
                    tags={row.titles ?? []}
                    {...(loadTitles === undefined
                      ? {}
                      : { loadSuggestions: (query: string) => loadTitles(index, query) })}
                    // ⚠️ THE SAME GROUPED PANEL AS THE CANDIDATE BUILDER'S POSITION FIELD, and
                    // for the same reason: `template-url="titles-with-classes.title-class-ux.
                    // typeahead.tpl.html"` (`client-contact-search.page.tpl.html:101`). This
                    // rendered the FLAT list — measured against build 354, ours came back as a
                    // bare `.autocomplete` where production's is
                    // `autocomplete nicer-scroller four-px-scroll
                    // title-with-classes-title-class-ux-auto-complete`, so the Extended
                    // Categories / Specific Titles split and the class pills were both absent.
                    renderSuggestions={TitleSuggestionPanel}
                    // `template="other-positions.title-class-ux.ng-tags.tpl.html"` — the
                    // contact form has no flag on this one either.
                    tagTemplate="position-ux"
                    onAdd={(tag) => {
                      onTitlesChange?.(index, [...(row.titles ?? []), tag])
                    }}
                    // ⚠️ FREE TEXT IS STILL OFF, which is upstream's shape for this
                    // which is what `add-from-autocomplete-only="true"` means: a tag comes
                    // from the list or not at all. The list itself is the loader above — it
                    // was missing, which is why this field offered nothing to pick.
                    onRemove={(at) => {
                      onTitlesChange?.(index, (row.titles ?? []).filter((_, i) => i !== at))
                    }}
                  />
                  {/* `client-contact-search.page.tpl.html:106` — a row with no titles, but
                      ONLY once a second row exists: `titleCriteria.titles.length > 1 &&
                      !isSingleTitleCriteriaValid(row)`. A single empty row is the form's
                      own starting state and is never an error. The `leftoverText` half of
                      upstream's condition needs the typeahead's uncommitted text, which
                      `TagsInput` does not expose, so it is not ported. */}
                  {(titleRows?.length ?? 0) > 1 && (row.titles?.length ?? 0) === 0 ? (
                    <label className="control-label has-error mB10">Please select positions</label>
                  ) : null}
                </div>
                {showCategoryTip(row.titles, recommendations[index] ?? []) ? <CategoryTip /> : null}
                {/* ⚠️ NO FLAG ON THIS ONE. The candidate form guards the block with
                    `extendedTitleClassUXFeatureEnabled`; this template's `ng-if` is only
                    `titles.titles.length > 0`, so once the row has a title the block is there
                    whatever the answer — including "No recommended titles". */}
                {(row.titles?.length ?? 0) > 0 ? (
                  <TitleRecommendations
                    rows={recommendations[index] ?? []}
                    loading={recommendationsLoading}
                    onSelect={(title) => onSelectRecommendation?.(index, title)}
                  />
                ) : null}
                {index < titleRows.length - 1 ? (
                  <div className="mT10">
                    <label className="bold-soft logical-operator-clauses">AND</label>
                  </div>
                ) : null}
              </div>
              <div
                className="remove-criteria-container criteria-cell"
                role="button"
                tabIndex={0}
                onClick={() => {
                  onRemoveTitleRow?.(index)
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') onRemoveTitleRow?.(index)
                }}
              >
                {/* Two stacked icons: a filled circle behind a thin ✕. One glyph would be the
                    wrong shape and the wrong colour — the circle carries the red. */}
                <div className="fa-stack fa-1x icons-container">
                  <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
                  <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
                </div>
              </div>
            </div>
          ))}
          <div className="criteria-row bottom-cta-row sub-section">
            <div className="add-foe-button criteria-cell">
              <a
                href=""
                className="add-button"
                onClick={(event) => {
                  event.preventDefault()
                  onAddTitleRow?.()
                }}
              >
                <i className="fal fa-plus-circle" />
                <span>&nbsp;Add</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
